/* eslint-disable sonarjs/no-nested-functions, unicorn/no-null */

import { useCallback } from 'react';
import { modals } from '@mantine/modals';
import { useShallow } from 'zustand/react/shallow';
import type { TOpenFile } from './DesignerFiles.store';
import useDesignerFilesStore from './DesignerFiles.store';
import { FileSaveConflictError, useSaveFileMutation } from './Files.queries';
import { saveDirtyFiles } from './FilesTab.utils';
import FileSaveConflictModal from './FileSaveConflictModal';

export const useSaveDirtyFilesWithConflictResolution = () => {
    const saveFileMutation = useSaveFileMutation();
    const { markDirty, setBaseGeneration } = useDesignerFilesStore(
        useShallow((state) => ({
            markDirty: state.markDirty,
            setBaseGeneration: state.setBaseGeneration
        }))
    );

    const resolveSaveConflict = useCallback(
        async (
            error: FileSaveConflictError,
            file: TOpenFile
        ): Promise<boolean> =>
            await new Promise((resolve) => {
                const id = modals.open({
                    title: 'File changed elsewhere',
                    centered: true,
                    children: (
                        <FileSaveConflictModal
                            filename={error.path.split('/').pop() ?? error.path}
                            onCancel={() => {
                                modals.close(id);
                                resolve(false);
                            }}
                            onReload={() => {
                                modals.close(id);
                                markDirty(error.path, false);
                                setBaseGeneration(error.path, null);
                                resolve(true);
                            }}
                            onSaveAnyway={() => {
                                saveFileMutation.mutate(
                                    {
                                        path: error.path,
                                        content: file.content,
                                        force: true
                                    },
                                    {
                                        onError: () => {
                                            resolve(false);
                                        },
                                        onSettled: () => {
                                            modals.close(id);
                                        },
                                        onSuccess: () => {
                                            resolve(true);
                                        }
                                    }
                                );
                            }}
                        />
                    )
                });
            }),
        [markDirty, saveFileMutation, setBaseGeneration]
    );

    return useCallback(
        async (dirtyFilePaths: string[]): Promise<boolean> =>
            await saveDirtyFiles(
                dirtyFilePaths,
                useDesignerFilesStore.getState().openFiles,
                saveFileMutation.mutateAsync,
                resolveSaveConflict
            ),
        [resolveSaveConflict, saveFileMutation]
    );
};
